const HEALTHY_BAR = "#b7d8c5";
const DEGRADED_BAR = "#9b650e";

const INTERVALS = [13, 17, 15, 18, 17, 21, 19, 16, 18, 20, 18, 21, 22, 23, 24, 24];

/**
 * Builds the 16-bar health strip used on each service card.
 * @param {number | null} degradedFrom Index where bars switch to the degraded color.
 * @returns {{ height: number, color: string }[]}
 */
function healthIntervals(degradedFrom = null) {
  return INTERVALS.map((height, index) => ({
    height,
    color:
      degradedFrom != null && index >= degradedFrom ? DEGRADED_BAR : HEALTHY_BAR,
  }));
}

/**
 * Illustrative snapshot from the Health Dashboard Figma frame (node 2:816).
 * This is demo data. It is not a live reading from GitHub, deploys, or monitoring.
 */
export const healthSnapshot = {
  title: "App health & releases",
  dataBadge: "Demo data",
  subtitle: "Production overview · 3 services · 1 release in progress",
  environment: "Production",
  region: "All regions",
  capturedAt: "Snapshot: Oct 18, 2026 · 14:32 UTC",
  serviceSummary: "2 healthy · 1 degraded",
  freshness: "Last 24 hours · refreshed 32s ago",
  services: [
    {
      id: "mobile",
      name: "Mobile app",
      platform: "iOS & Android",
      icon: "smartphone",
      status: "healthy",
      statusLabel: "Healthy",
      metric: "99.96%",
      metricLabel: "Crash-free sessions",
      detail: "App start, p95   1.2 s",
      sloLabel: "Stability SLO",
      sloValue: "99.96% / 99.9% target",
      version: "v5.12.1 · 100% rollout",
      updated: "32s ago",
      intervals: healthIntervals(),
    },
    {
      id: "desktop",
      name: "Desktop app",
      platform: "macOS & Windows",
      icon: "monitor",
      status: "healthy",
      statusLabel: "Healthy",
      metric: "99.99%",
      metricLabel: "Crash-free sessions",
      detail: "App start, p95   840 ms",
      sloLabel: "Stability SLO",
      sloValue: "99.99% / 99.9% target",
      version: "v3.8.4 · 100% rollout",
      updated: "32s ago",
      intervals: healthIntervals(),
    },
    {
      id: "api",
      name: "API",
      platform: "Public REST API",
      icon: "server",
      status: "degraded",
      statusLabel: "Degraded",
      metric: "482 ms",
      metricLabel: "Response latency, p95",
      detail: "5xx errors   0.18%   ·   p95 target < 300 ms",
      detailTone: "warning",
      sloLabel: "Uptime SLO",
      sloValue: "99.95% / 99.9% target",
      version: "v2.18.0 · 25% canary",
      updated: "32s ago",
      intervals: healthIntervals(11),
    },
  ],
  release: {
    statusLabel: "Rollout paused",
    repository: "orbit/api",
    version: "v2.18.0",
    ref: "· main · 8f4c2a1",
    linkLabel: "View GitHub release",
    timing: "Started 13:48 UTC · updated 1m ago",
    stages: [
      {
        id: "checks",
        title: "Checks & build",
        detail: "18/18 checks passed",
        timing: "13:52 UTC · Complete",
        status: "complete",
      },
      {
        id: "staging",
        title: "Staging",
        detail: "Smoke tests passed",
        timing: "14:06 UTC · Complete",
        status: "complete",
      },
      {
        id: "approval",
        title: "Approval",
        detail: "Approved by A. Chen",
        timing: "14:14 UTC · Complete",
        status: "complete",
      },
      {
        id: "production",
        title: "Production rollout",
        detail: "Paused at 25% traffic",
        timing: "14:26 UTC · Guardrail hit",
        status: "paused",
      },
    ],
    rolloutLabel: "Production · canary",
    rolloutValue: "25% of traffic",
    rolloutPercent: 25,
    guardrailTitle: "Latency guardrail exceeded",
    guardrailDetail: "Resume after p95 stays below 300 ms for 10 minutes.",
    rollbackLabel: "Rollback available",
    previousStable: "Previous stable: v2.17.3",
  },
  incident: {
    countLabel: "1 open",
    linkLabel: "Incident details",
    severity: "SEV 3",
    id: "INC-284",
    state: "Investigating",
    title: "Elevated API latency in us-east-1",
    summary:
      "Canary paused; team is investigating connection pool saturation. Existing traffic remains available.",
    ownerInitials: "ML",
    owner: "Maya Lee · API on-call",
    updated: "Updated 2m ago",
  },
  safeguards: {
    summary: "3 passed · 1 holding",
    checks: [
      {
        id: "security",
        label: "Security scan",
        value: "0 critical / high",
        status: "passed",
      },
      {
        id: "reviewers",
        label: "Required reviewers",
        value: "2 of 2 approved",
        status: "passed",
      },
      {
        id: "rollback",
        label: "Rollback artifact",
        value: "v2.17.3 retained",
        status: "passed",
      },
      {
        id: "latency",
        label: "Production latency gate",
        value: "Holding",
        status: "holding",
      },
    ],
    owner: "Release owner: Alex Chen · Platform Engineering",
  },
  footer: {
    notice: "Illustrative snapshot only. No live monitoring or GitHub connection.",
    window: "Health: last 24h · SLO: rolling 30d",
  },
};
